import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';

import { Tab } from '../../../components/Tab';
import { Tabs } from '../../../components/Tabs';
import TabPanel from '../../../components/TabPanel';
import useBundleRecommendationContext from '../../../context/BundleRecommendation/use-bundle-recommendation-context';
import useLocation from '../../../hooks/useLocation';

import { PromotionDisclaimer } from '../../bundles/components/promotion-disclaimer';

import { GroupTicketPurchase } from './group-ticket-purchase';
import { IndividualTicketPurchase } from './individual-ticket-purchase';
import TicketPurchaseTabTooltip from './ticket-purchase-tab-tooltip';

interface Props {
  onSubmit?: () => void /** Exposes a callback to let the father component know when individual or group tickets purchase forms are submitted */;
}

export default function TicketPurchaseTabs({ onSubmit }: Props) {
  /* === STATES ======================================================================= */
  const { t } = useTranslation();
  const { language: locale } = useI18next();

  const { location, isDone, isError } = useLocation();
  const requestCountry = location?.request_country ?? 'US';

  const { ticketMessages } = useBundleRecommendationContext();

  const [value, setValue] = useState(0);

  /* === HANDLERS ===================================================================== */
  const handleChange = (_, newValue: number) => {
    setValue(newValue);
  };

  /* === RENDER ======================================================================= */
  return (
    <Box sx={{ width: '100%' }}>
      <Tabs value={value} onChange={handleChange} aria-label="ticket-purchase-tabs" variant="scrollable" scrollButtons="auto">
        <Tab label={t('tickets_service.single_ticket.name')} icon={<TicketPurchaseTabTooltip />} iconPosition="end" />
        <Tab icon={<TicketPurchaseTabTooltip />} label={t('tickets_service.group_rates.name')} iconPosition="end" />
      </Tabs>

      {
        /** IMPORTANT: Ticket Purchase Forms (Individual and Group) MUST be mounted until the process to get country is done (or error),
         *             to can compute the default values correctly (based on country). */
        isDone || isError ? (
          <>
            <TabPanel value={value} index={0}>
              <IndividualTicketPurchase country={requestCountry} onSubmit={onSubmit} />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <GroupTicketPurchase country={requestCountry} onSubmit={onSubmit} />
            </TabPanel>
          </>
        ) : null
      }

      {ticketMessages?.form && <PromotionDisclaimer product="ticket">{ticketMessages.form[locale]}</PromotionDisclaimer>}
    </Box>
  );
}
